/**
 * Trang quản lý liên hệ admin — danh sách yêu cầu liên hệ từ khách hàng.
 *
 * Tính năng:
 * - Tìm kiếm và lọc theo trạng thái (mới/đã đọc/đã trả lời).
 * - Cập nhật trạng thái từng liên hệ; xem thông tin BĐS liên quan qua modal.
 * - Phân trang qua useAdminContacts.
 */
import { useMemo, useState } from 'react';
import { AdminContactCard } from '../../components/admin/AdminContactCard';
import { AdminContactsSkeleton } from '../../components/admin/AdminContactsSkeleton';
import { AdminPropertyInfoModal } from '../../components/admin/AdminPropertyInfoModal';
import { EmptyState } from '../../components/Common/EmptyState';
import { Pagination } from '../../components/Common/Pagination';
import { useAdminContacts } from '../../features/admin/contacts/hooks/useAdminContacts';
import { formatText, TEXT } from '../../shared/languages';

/** Bộ lọc trạng thái nhanh trên toolbar */
const QUICK_STATUS_FILTERS = [
  { value: '', labelKey: 'filterAll' },
  { value: 'new', labelKey: 'filterNew' },
  { value: 'read', labelKey: 'filterRead' },
  { value: 'replied', labelKey: 'filterReplied' },
];

/** Trang danh sách liên hệ khách hàng trong khu vực admin */
export const AdminContactsPage = () => {
  const {
    contactList,
    pagination,
    filters,
    searchInput,
    setSearchInput,
    isLoadingContacts,
    updatingContactId,
    hasActiveFilters,
    updateFilter,
    changePage,
    updateContactStatus,
  } = useAdminContacts();
  /** State modal xem thông tin BĐS gắn với liên hệ */
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [previewProperty, setPreviewProperty] = useState(null);
  const admin = TEXT.admin;
  const contactsText = admin.contacts;
  const contactStatus = admin.contactStatus;
  const common = TEXT.common;

  const summaryLabel = useMemo(
    () =>
      formatText(contactsText.resultsCount, {
        count: pagination?.total ?? contactList.length,
      }),
    [contactList.length, contactsText.resultsCount, pagination?.total]
  );

  const handleStatusChange = async (contact, status) => {
    await updateContactStatus(contact.id, status);
  };

  const handleViewPropertyInfo = (contact) => {
    setPreviewProperty(contact.property || null);
    setSelectedPropertyId(contact.property_id);
  };

  const handleClosePropertyInfo = () => {
    setSelectedPropertyId(null);
    setPreviewProperty(null);
  };

  // Skeleton toàn trang khi lần đầu tải (chưa có filter)
  if (isLoadingContacts && !contactList.length && !searchInput && !filters.status) {
    return <AdminContactsSkeleton />;
  }

  return (
    <div className="admin-contacts-page">
      <div className="admin-heading admin-contacts-heading">
        <div className="admin-contacts-heading-copy">
          <span className="eyebrow">{admin.contactsEyebrow}</span>
          <h1>{admin.contactsTitle}</h1>
          <p className="admin-contacts-subtitle">{summaryLabel}</p>
        </div>
      </div>

      <div className="admin-contacts-toolbar">
        <label className="admin-contacts-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            inputMode="search"
            enterKeyHint="search"
            role="searchbox"
            name="admin-contacts-filter"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={contactsText.searchPlaceholder}
          />
        </label>

        <div className="admin-contacts-filter-row">
          {QUICK_STATUS_FILTERS.map((item) => (
            <button
              key={item.value || 'all'}
              type="button"
              className={`admin-contacts-chip ${filters.status === item.value ? 'is-active' : ''}`}
              onClick={() => updateFilter('status', item.value)}
            >
              {contactsText[item.labelKey]}
            </button>
          ))}
        </div>
      </div>

      {isLoadingContacts && !contactList.length ? (
        <div className="admin-contact-list admin-contact-list--loading">
          {Array.from({ length: 3 }).map((_, index) => (
            <article className="admin-contact-card admin-contact-card--skeleton" key={index} aria-hidden="true">
              <span className="skeleton title" />
              <span className="skeleton text" />
              <span className="skeleton text short" />
            </article>
          ))}
        </div>
      ) : contactList.length ? (
        <>
          <div className="admin-contact-list">
            {contactList.map((contact) => (
              <AdminContactCard
                key={contact.id}
                contact={contact}
                contactStatus={contactStatus}
                contactsText={contactsText}
                admin={admin}
                common={common}
                isUpdating={updatingContactId === contact.id}
                onStatusChange={handleStatusChange}
                onViewProperty={handleViewPropertyInfo}
              />
            ))}
          </div>

          <Pagination
            pagination={pagination}
            isLoading={isLoadingContacts}
            onPageChange={changePage}
          />
        </>
      ) : (
        <EmptyState
          title={hasActiveFilters ? contactsText.noResultsTitle : admin.contactsEmptyTitle}
          message={
            hasActiveFilters ? contactsText.noResultsMessage : admin.contactsEmptyMessage
          }
        />
      )}

      <AdminPropertyInfoModal
        isOpen={Boolean(selectedPropertyId)}
        property={previewProperty}
        propertyId={selectedPropertyId}
        onClose={handleClosePropertyInfo}
      />
    </div>
  );
};
